import {
  CHART_SPEC_VERSION,
  ChartSpec,
  CompareMode,
  DateRangeSpec,
  placeBreakdown,
} from '@employ-engage/data';

/**
 * Default charts for a survey.
 *
 * The builder never opens on an empty canvas. A page that asks you to pick a
 * metric, a dimension and a visualisation before showing anything gets demoed
 * once and used never; one that shows every question straight away and lets
 * you adjust gets used weekly. So the first thing an admin does here is "pin
 * this" or "make it a pie", not "configure this from scratch".
 */

export type ChartableField = {
  ref: Record<string, unknown>;
  label: string;
  questionType: string | null;
  presentInVersionIds: string[];
  isAnonymousSource: boolean;
  options: { label: string; score?: number | null; notApplicable?: boolean }[];
  wordings: { versionId: string; text: string }[];
  /** True once any option carries a score or a not-applicable flag. */
  isScored?: boolean;
  /** Two versions score the same option differently, so scores are ignored. */
  hasInconsistentScale?: boolean;
  /** Offered when the options match a known scale and nothing is scored yet. */
  suggestedScale?: {
    presetId: string;
    name: string;
    min: number;
    max: number;
    optionScores: { optionId: string; label: string; score: number | null }[];
  } | null;
};

/**
 * What to break every chart down by.
 *
 * The fixed values are facts about the response. A value of
 * `question:<questionKey>` breaks down by another question instead, which is
 * how the association cross-tab is reached: "of the people who answered B this
 * way, how did they answer A".
 */
/** Mirrors `CardViz` in chart-card.tsx; declared here to avoid an import cycle. */
export type CardViz =
  | 'bar'
  | 'horizontalBar'
  | 'stackedBar'
  | 'line'
  | 'pie'
  | 'donut'
  | 'table';

export type BreakdownKind =
  | 'none'
  | 'department'
  | 'month'
  | 'version'
  | `question:${string}`;

export const QUESTION_BREAKDOWN_PREFIX = 'question:';

/** The question key a `question:` breakdown names, if it names one. */
export const breakdownQuestionKey = (breakdown: BreakdownKind) =>
  breakdown.startsWith(QUESTION_BREAKDOWN_PREFIX)
    ? breakdown.slice(QUESTION_BREAKDOWN_PREFIX.length)
    : null;

/** A stable handle for a field, used as the breakdown's value. */
export const fieldKey = (field: ChartableField) =>
  String(field.ref.questionId ?? field.ref.questionKey ?? field.label);

/**
 * Only a scored single-choice question can be a breakdown.
 *
 * Not a restriction for its own sake: an unscored question has no threshold to
 * collapse at, so it would cross-tab as one column per option — five by five
 * is twenty-five cells, and on an anonymous survey almost all of them fall
 * under the minimum-count floor and go dark. A multi-select is worse, because
 * one person lands in several columns and the percentages stop adding up.
 */
export const canBeBreakdown = (field: ChartableField) =>
  field.questionType === 'select one' &&
  !!field.isScored &&
  !field.hasInconsistentScale;

/**
 * What the card shows.
 *
 * `distribution` charts the answers themselves — the only option for an
 * unscored question. The scored ones turn the question into a single figure
 * and chart it *by* the breakdown instead, which is why they change the shape
 * of the spec rather than just its measure.
 */
export type MeasureKind = 'distribution' | 'topBox' | 'mean';

export const MEASURE_LABELS: Record<MeasureKind, string> = {
  distribution: 'All answers',
  topBox: 'Agree or better',
  mean: 'Average score',
};

export const BREAKDOWN_FIELDS: Record<
  Exclude<BreakdownKind, 'none'>,
  Record<string, unknown>
> = {
  department: { kind: 'department', level: 0, label: 'Department' },
  month: {
    kind: 'time',
    field: 'dateCompleted',
    bucket: 'month',
    label: 'Month',
  },
  version: { kind: 'formVersion', label: 'Survey version' },
};

/**
 * What the card calls a table, the spec calls a pivot.
 *
 * `CardViz` is `ChartVizType | 'table'` and `ChartKind` has `'pivot'`; they
 * describe the same thing. Converting in one place keeps the two vocabularies
 * from being mapped ad hoc at each render site, which is how the dashboard
 * ended up not handling `'table'` at all.
 */
export const vizToChartKind = (viz: string | undefined) =>
  viz === 'table' ? 'pivot' : viz;

export const chartKindIsTable = (kind: string | undefined) =>
  kind === 'pivot' || kind === 'table';

/** Free-text questions have no buckets to count, so they are not plotted. */
export const isPlottable = (field: ChartableField) =>
  field.questionType === 'select one' || field.questionType === 'select many';

/**
 * Long option lists are unreadable as vertical bars, so anything with more
 * than a handful of choices defaults to horizontal.
 */
export const defaultVizFor = (field: ChartableField) =>
  field.options.length > 4 ? 'horizontalBar' : 'bar';

/** Scored measures only make sense once an author has scored the options. */
export const availableMeasures = (field: ChartableField): MeasureKind[] => {
  const scored =
    field.isScored &&
    !field.hasInconsistentScale &&
    field.questionType === 'select one';
  return scored ? ['distribution', 'topBox', 'mean'] : ['distribution'];
};

/**
 * The dimension a breakdown resolves to, or null for none.
 *
 * A question breakdown is collapsed to two buckets at its top-box threshold.
 * The alternative is a column per option, which at these sample sizes is a
 * grid of suppressed cells; two buckets publish, and "of the people below top
 * box on B, this is how they answered A" is the sentence anyone wants anyway.
 *
 * A question is never its own breakdown: the cross-tab would be diagonal and
 * say nothing, so that card falls back to no breakdown rather than showing a
 * meaningless one.
 */
export const breakdownDimensionFor = (
  breakdown: BreakdownKind,
  charted: ChartableField,
  fields: ChartableField[]
): Record<string, unknown> | null => {
  if (breakdown === 'none') return null;

  const questionKey = breakdownQuestionKey(breakdown);
  if (questionKey === null) {
    return {
      field:
        BREAKDOWN_FIELDS[
          breakdown as Exclude<BreakdownKind, 'none' | `question:${string}`>
        ],
    };
  }

  if (fieldKey(charted) === questionKey) return null;

  const target = (fields ?? []).find((f) => fieldKey(f) === questionKey);
  if (!target || !canBeBreakdown(target)) return null;

  return { field: target.ref, collapse: 'topBox' };
};

export const buildSpec = (
  templateId: string,
  field: ChartableField,
  options: {
    dateRange?: DateRangeSpec;
    breakdown?: BreakdownKind;
    asPercent?: boolean;
    measure?: MeasureKind;
    /** Needed only to resolve a `question:` breakdown to its field. */
    fields?: ChartableField[];
    /** What the card is showing. Stored, so a pin reproduces it. */
    viz?: CardViz;
    /**
     * Compare every figure with an earlier window of the same length.
     *
     * Carried on the spec rather than applied at render, so a pinned tile
     * keeps the comparison it was pinned with.
     */
    compareTo?: CompareMode | null;
  } = {}
): ChartSpec => {
  const breakdown = options.breakdown ?? 'none';
  const measure = options.measure ?? 'distribution';
  const breakdownDimension = breakdownDimensionFor(
    breakdown,
    field,
    options.fields ?? []
  );

  const base = {
    specVersion: CHART_SPEC_VERSION,
    source: { templateId },
    dateRange: options.dateRange ?? { mode: 'allTime' },
    filters: [],
    // Omitted entirely rather than set to null: a spec with no comparison
    // should look like every spec written before comparisons existed.
    ...(options.compareTo ? { compareTo: { mode: options.compareTo } } : {}),
  };

  if (measure !== 'distribution') {
    // A scored measure collapses the question into one figure, so the
    // breakdown becomes the axis rather than a second series. With no
    // breakdown there is nothing to plot against and it is a single number.
    //
    // Where it lands is decided by the shared `placeBreakdown`, which the
    // dashboard's `applyOverrides` also calls. Two implementations of this
    // rule drift silently: a tile would simply start showing a different
    // chart from the card it was pinned from.
    const scoredMeasure =
      measure === 'topBox'
        ? ({ kind: 'topBox', field: field.ref } as never)
        : ({ kind: 'mean', field: field.ref } as never);

    return {
      ...base,
      ...placeBreakdown({
        measure: scoredMeasure,
        chartedDimension: null,
        breakdown: (breakdownDimension as never) ?? null,
      }),
      measure: scoredMeasure,
      visualization: {
        // With no breakdown this is one number, and no chart type applies.
        kind: breakdownDimension
          ? vizToChartKind(options.viz) ?? 'horizontalBar'
          : 'stat',
        // An average of an ordinal scale does not deserve more than one
        // decimal place, and a top-box is a whole percentage.
        valueFormat: measure === 'mean' ? 'percent1' : 'percent0',
      },
    } as ChartSpec;
  }

  const chartedDimension = {
    field: field.ref as never,
    // A scored question has a natural order, and showing it by popularity
    // instead reads as nonsense on a scale.
    ...(field.isScored
      ? { sort: { by: 'scaleOrder', direction: 'desc' } }
      : {}),
  };
  const distributionMeasure = (
    options.asPercent
      ? // Of respondents, not of answers: with a multi-select one person can
        // appear in several buckets, and dividing by answers would make the
        // number mean nothing.
        { kind: 'respondentPercent', of: 'total' }
      : { kind: 'responseCount' }
  ) as never;

  return {
    ...base,
    ...placeBreakdown({
      measure: distributionMeasure,
      chartedDimension: chartedDimension as never,
      breakdown: (breakdownDimension as never) ?? null,
    }),
    measure: options.asPercent
      ? // Of respondents, not of answers: with a multi-select one person can
        // appear in several buckets, and dividing by answers would make the
        // number mean nothing.
        { kind: 'respondentPercent', of: 'total' }
      : { kind: 'responseCount' },
    visualization: {
      // The user's choice wins. Without this the spec recorded whatever the
      // option count implied, so pinning a pie stored — and the dashboard
      // drew — a bar.
      kind:
        vizToChartKind(options.viz) ??
        (breakdownDimension ? 'stackedBar' : defaultVizFor(field)),
      valueFormat: options.asPercent ? 'percent0' : 'integer',
    },
  } as ChartSpec;
};

/** One sensible chart per question, in the order the survey asks them. */
export const autoSpecsForTemplate = (
  templateId: string,
  fields: ChartableField[],
  options: { dateRange?: DateRangeSpec } = {}
) =>
  (fields ?? [])
    .filter(isPlottable)
    .map((field) => ({ field, spec: buildSpec(templateId, field, options) }));
